import moment, { Moment } from 'moment';
import React, { forwardRef, ReactNode, useImperativeHandle, useMemo } from 'react';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import { Flex } from '../Flex';
import { Pane } from '../Pane';
import { Calendar } from './Calendar';
import { DateRangePickerContext, TimeDisplayMode } from './context';
import { DateRangeInput } from './DateRangeInput';
import { useDateRangeFocus } from './hooks';
import css from './index.module.scss';
import { DateRanges, Focus, MomentRanges, RangeIndex } from './types';
import { dateRangeToMomentRange } from './util';

export interface DateRangePickerProps {
  timeDisplayMode?: TimeDisplayMode;
  /**
   * The selected/comparison date ranges. A day will be rendered differently
   * based on if/where it falls within the provided ranges.
   */
  ranges: DateRanges;
  /**
   * Function that is called when the ranges change.
   */
  onChange: (ranges: DateRanges) => void;
  /**
   * The minimum selectable date. Defaulted to 18 months in the past
   */
  minDate?: Date;
  /**
   * The maximum selectable date for display. Time will default to the start of the day, but can be overridden.
   */
  maxDate?: Date;
  /**
   * Optional function that controls whether a given day is disabled. Useful for
   * limiting selection to a minimum of 3 days, for example.
   */
  isDayDisabled?: (day: Date) => boolean;
  /**
   * Optional function to render an info tooltip for a given day. Useful for visualizing
   * an orgs retention period, for example.
   */
  renderInfoTooltip?: (day: Date) => ReactNode;

  /**
   * If `true`, all inputs and days are readOnly.
   *
   * @default false
   */
  readOnly?: boolean;

  /**
   * If `true`, dates will be in UTC instead of the user's local timezone.
   *
   * @default false
   */
  utc?: boolean;

  /**
   * If `true`, the second value in the date range picker will be set to 'ongoing'. The value will not be able to be modified.
   * @default false
   */
  ongoing?: boolean;

  /**
   * If `false`, and the end date is at midnight, we will adjust the end date to be forward one day. This is due to how ranges work in solr.
   * For example, if we want the end date to be 8/7 then we set the end date to 8/8 at 12am so that the entire day of 8/7 is included.
   * @default false
   */
  preserveRangeEnd?: boolean;

  /**
   * If `true`, the date range picker will render clock time inputs for each date input.
   * @default false
   */
  includeTimeInputs?: boolean;

  /**
   * Optional flag to enable the new calendar dimensions.
   * TODO(jb): Delete along with `AppDatePickerTimeInputs` | `app-date-picker-time-inputs`
   * */
  v2?: boolean;
}

export interface DateRangePickerHandle {
  focus: (focus: Focus) => void;
}

const defaultMinDate = moment().subtract(18, 'months');
const defaultMaxDate = moment().endOf('day');

const DateRangePicker = forwardRef<DateRangePickerHandle, DateRangePickerProps>(
  (
    {
      timeDisplayMode = 'auto',
      ranges,
      onChange,
      minDate,
      maxDate,
      isDayDisabled,
      renderInfoTooltip,
      readOnly = false,
      utc = false,
      ongoing = false,
      preserveRangeEnd = false,
      includeTimeInputs = false,
      v2,
    },
    ref,
  ) => {
    const [primaryRange, comparisonRange] = ranges;
    const disableComparison = !comparisonRange;
    const momentRanges: MomentRanges = useMemo(
      () => [
        dateRangeToMomentRange(primaryRange, utc, preserveRangeEnd),
        dateRangeToMomentRange(comparisonRange, utc, preserveRangeEnd),
      ],
      [primaryRange, utc, preserveRangeEnd, comparisonRange],
    );

    const displayRanges: MomentRanges = useMemo(() => {
      // if ongoing we want the range to extend an extra day so the calendar appears to go on forever
      if (ongoing) {
        const primary = momentRanges[RangeIndex.Primary];
        return [
          {
            start: primary.start,
            end: moment(primary.end).add(1, 'day'),
          },
          momentRanges[RangeIndex.Comparison],
        ];
      } else {
        return momentRanges;
      }
    }, [momentRanges, ongoing]);

    const minMaxMoment = useMemo(() => {
      const makeMoment = utc ? moment.utc : moment;
      return {
        min: minDate ? makeMoment(minDate) : defaultMinDate,
        max: maxDate ? makeMoment(maxDate) : defaultMaxDate,
      };
    }, [maxDate, minDate, utc]);

    const { focus, inputRefs, onDayChange, onFocus, focusNext } = useDateRangeFocus({
      ranges: momentRanges,
      onChange,
      disableComparison,
      minMaxMoment,
      ongoing,
      preserveRangeEnd,
      includeTimeInputs,
    });

    useImperativeHandle(ref, () => ({
      focus: onFocus,
    }));

    const isMomentDayDisabled = useMemo(
      () => (!isDayDisabled ? undefined : (day: Moment) => isDayDisabled(day.toDate())),
      [isDayDisabled],
    );
    const INPUTS_HEIGHT = disableComparison ? 64 : 112;

    const renderMomentInfoTooltip = useMemo(
      () => (!renderInfoTooltip ? undefined : (day: Moment) => renderInfoTooltip(day.toDate())),
      [renderInfoTooltip],
    );

    /**
     * TODO(jcarroll2007): consider adding more information into the context to simplify the prop drilling that existed before
     * adding the context.
     */
    const contextValue = useMemo(
      () => ({
        timeDisplayMode,
        readOnly,
        includeTimeInputs,
        v2,
      }),
      [timeDisplayMode, readOnly, includeTimeInputs, v2],
    );

    return (
      <DateRangePickerContext.Provider value={contextValue}>
        <Pane
          data-component="DateRangePicker"
          width="min-content"
          height={1}
          testId="date-range-picker"
          {...identifyComponent('date-range-picker')}
        >
          <Flex column alignItems="stretch" style={{ height: '100%' }}>
            <Box
              className={includeTimeInputs ? css.rangesWithTime : css.ranges}
              height={INPUTS_HEIGHT}
              padding={8}
            >
              <DateRangeInput
                ref={inputRefs[RangeIndex.Primary]}
                range={momentRanges[RangeIndex.Primary]}
                rangeIndex={RangeIndex.Primary}
                onChange={onDayChange}
                onFocus={onFocus}
                onFocusNext={focusNext}
                ongoing={ongoing}
              />
              {!disableComparison && (
                <DateRangeInput
                  ref={inputRefs[RangeIndex.Comparison]}
                  range={momentRanges[RangeIndex.Comparison]}
                  rangeIndex={RangeIndex.Comparison}
                  onChange={onDayChange}
                  onFocus={onFocus}
                  onFocusNext={focusNext}
                />
              )}
            </Box>
            <Box height={`calc(100% - ${INPUTS_HEIGHT}px)`}>
              <Calendar
                ranges={displayRanges}
                focus={focus}
                onDayClick={onDayChange}
                minDate={minMaxMoment.min}
                maxDate={minMaxMoment.max}
                isDayDisabled={isMomentDayDisabled}
                renderInfoTooltip={renderMomentInfoTooltip}
              />
            </Box>
          </Flex>
        </Pane>
      </DateRangePickerContext.Provider>
    );
  },
);
DateRangePicker.displayName = 'DateRangePicker';

const MemoizedDateRangePicker = React.memo(DateRangePicker);

export { MemoizedDateRangePicker as DateRangePicker };
